import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { RootPaths } from "@src/appV2/App/paths";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { useOpenShifts } from "@src/appV2/OpenShifts/api/useOpenShifts";
import { useGetBookabilityStatusForShifts } from "@src/appV2/OpenShifts/ShiftAction/api/useGetBookabilityStatusForShifts";
import { RateNegotiationToolLocationState } from "@src/appV2/redesign/Shift/types";
import { SHIFT_DISCOVERY_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { LegacyShift } from "@src/lib/interface/src";
import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { NegotiationCommitType } from "./interfaces";
import { INegotiationCommitModalProps } from "./interfaces";
import { NegotiationCommitModal } from "./NegotiationCommitModal";
import { NegotiationTool } from "./negotiationTool";

interface RateNegotiationToolContainerPathParams {
  shiftId: string;
}
/**
 * This function servers as a bridge between appV2 and legacy rate negotiation flow.
 * As we have whole flow implemented as modals, we cannot use it in appV2 as we cannot import legacy code in appV2.
 * This component is accesible via route and will render legacy rate negotiation flow.
 */
export function RateNegotiationToolContainerPage() {
  const history = useHistory();

  const modalState = useModalState("open");
  const negotiationCommitModalState = useModalState();

  const { userId: workerId } = useDefinedWorker();
  const { shiftId } = useParams<RateNegotiationToolContainerPathParams>();

  const { showErrorToast } = useToast();

  const [requestLoading, setRequestLoading] = useState(false);

  const [negotiationCommitModalProps, setNegotiationCommitModalProps] =
    useState<INegotiationCommitModalProps>({
      modalType: NegotiationCommitType.PROPOSE_NEW_RATE,
      closeModal: () => {},
      onCommit: () => {},
      requestLoading: false,
    });

  const {
    data: shiftData,
    isSuccess: isShiftSuccess,
    isError: isShiftError,
  } = useOpenShifts({ id: [shiftId] });

  const { linkSource: linkSourceParam } = useSearchParams();
  const location = useLocation<RateNegotiationToolLocationState>();

  const { shiftBookabilityById, queryResult: bookabilityStatusQueryResult } =
    useGetBookabilityStatusForShifts({ shiftIds: [shiftId], agentId: workerId });

  const shift = shiftData?.openAgentShifts.find((s) => s._id === shiftId);

  const { isLoading: isShiftBookabilityLoading } = bookabilityStatusQueryResult;

  useEffect(() => {
    if (isShiftError || (isShiftSuccess && !isDefined(shift))) {
      // Temporary log to check when open shifts are fetched but shift is not returned
      if (isShiftSuccess && !isDefined(shift)) {
        logError(APP_V2_APP_EVENTS.RATE_NEGOTIATION_SHIFT_NOT_FOUND, {
          error: new Error("Open shifts fetched but shift not returned"),
          metadata: {
            shiftId,
          },
        });
      }
      showErrorToast("Error fetching shift data");
      history.goBack();
    }
  }, [isShiftSuccess, showErrorToast, history, isShiftError, shift, shiftId]);

  if (!isDefined(shift)) {
    return null;
  }

  const isWorkerBookedForShift = shift.agentId === workerId;

  return (
    <>
      <NegotiationTool
        isOpen={modalState.modalIsOpen}
        closeModal={() => history.goBack()}
        shift={shift as unknown as LegacyShift}
        shiftBookability={shiftBookabilityById[shiftId]?.attributes.bookability}
        isLoadingShiftBook={isShiftBookabilityLoading}
        workApproved={isWorkerBookedForShift}
        updateShiftRateNegotiation={(updatedShift) => {
          history.goBack();
        }}
        onBookShift={(bookedShift) => {
          if (location.state.origin === "SHIFT_DETAILS_SHEET") {
            // We navigate to shifts discovery page instead of going back to shift booking details
            history.replace(`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_PATH}`);
            return;
          }
          history.goBack();
        }}
        showCommitModal={(props) => {
          setNegotiationCommitModalProps(props);

          negotiationCommitModalState.setModalStatus(props.isOpen ? "open" : "closed");
        }}
        closeCommitModal={() => {
          negotiationCommitModalState.closeModal();
        }}
        setRequestLoading={setRequestLoading}
        hcfProfileSource={typeof linkSourceParam === "string" ? linkSourceParam : undefined}
      />

      <NegotiationCommitModal
        {...negotiationCommitModalProps}
        modalState={negotiationCommitModalState}
        closeModal={() => history.goBack()}
        requestLoading={requestLoading}
      />
    </>
  );
}
